import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet';

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1
    }
  }
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.5 }
  }
};

export default function PrivacyPolicy() {
  return (
    <>
      <Helmet>
        <title>Privacy Policy - BridgeOnce</title>
        <meta 
          name="description" 
          content="BridgeOnce's privacy policy details how we collect, use, and protect your personal information."
        />
        <link rel="canonical" href="https://bridgeonce.com/privacy" />
      </Helmet>

      <main className="pt-24 bg-background">
        {/* Breadcrumb */}
        <div className="max-w-7xl mx-auto px-6 py-4">
          <nav className="text-sm text-text/60">
            <a href="/" className="hover:text-primary">Home</a>
            <span className="mx-2">/</span>
            <span className="text-text">Privacy Policy</span>
          </nav>
        </div>

        <motion.div 
          className="max-w-4xl mx-auto px-6 py-12"
          variants={containerVariants}
          initial="hidden"
          animate="visible"
        >
          {/* Header */}
          <motion.div 
            className="text-center mb-16"
            variants={itemVariants}
          >
            <h1 className="text-4xl lg:text-5xl font-bold text-primary mb-6">
              Privacy Policy
            </h1>
            <p className="text-xl text-text/80">
              Last Updated: January 18, 2025
            </p>
          </motion.div>

          {/* Content */}
          <motion.div 
            className="prose prose-lg max-w-none"
            variants={itemVariants}
          >
            <div className="bg-white rounded-2xl shadow-lg p-8 mb-8">
              <p className="text-text/80 mb-6">
                If you have any questions, concerns, or are unclear about any part of this policy, 
                please contact us using the Contact Information section at the bottom of the page.
              </p>

              {/* Table of Contents */}
              <div className="bg-background rounded-xl p-6 mb-8">
                <h2 className="text-xl font-semibold text-primary mb-4">Table of Contents</h2>
                <ul className="space-y-2">
                  {[
                    'General Overview',
                    'Cookie Policy',
                    'Information We Collect',
                    'Information Usage',
                    'Information Security',
                    'Access, Modify or Delete Your Personal Information',
                    'California Privacy Rights',
                    'GDPR Rights',
                    'Contact Information'
                  ].map((item) => (
                    <li key={item}>
                      <a 
                        href={`#${item.toLowerCase().replace(/\s+/g, '-')}`}
                        className="text-secondary hover:text-primary transition-colors duration-200"
                      >
                        {item}
                      </a>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Policy Sections */}
              {[
                {
                  id: 'general-overview',
                  title: 'General Overview',
                  content: (
                    <>
                      <p>BridgeOnce created this privacy statement to demonstrate our commitment to protecting your privacy. The following discloses our information gathering and dissemination practices for our services. If we make material changes to this policy, we will notify you here, by email, or by means of a prominent notice on our website.</p>
                      <p className="mt-4">By accepting these terms, you consent to our use and disclosure of your personal information in accordance with this Privacy Policy. This Privacy Policy is incorporated into and subject to our Terms of Use.</p>
                    </>
                  )
                },
                {
                  id: 'cookie-policy',
                  title: 'Cookie Policy',
                  content: (
                    <>
                      <p>When you visit our website, we use cookies and similar technologies for the following purposes:</p>
                      <div className="mt-4 space-y-6">
                        <div>
                          <h3 className="font-semibold mb-2">Essential Cookies:</h3>
                          <ul className="list-disc pl-5 space-y-1">
                            <li>Required for basic website functionality and security</li>
                            <li>Used for authentication and form submission</li>
                            <li>Cannot be disabled as they are necessary for the website to function</li>
                          </ul>
                        </div>
                        <div>
                          <h3 className="font-semibold mb-2">Analytics Cookies:</h3>
                          <ul className="list-disc pl-5 space-y-1">
                            <li>Help us understand how visitors use our site</li>
                            <li>Collect anonymous statistical data</li>
                            <li>Can be disabled through your browser settings</li>
                          </ul>
                        </div>
                        <div>
                          <h3 className="font-semibold mb-2">Third-Party Cookies:</h3>
                          <ul className="list-disc pl-5 space-y-1">
                            <li>Used by our service providers (Stripe, Firebase, Formspree)</li>
                            <li>Managed according to their respective privacy policies</li>
                            <li>Used for payment processing and form handling</li>
                          </ul>
                        </div>
                        <div>
                          <h3 className="font-semibold mb-2">Cookie Consent:</h3>
                          <p>When you first visit our website, you'll be asked to accept or decline non-essential cookies. You can change these preferences anytime through our Cookie Settings. Your choices will be respected and saved.</p>
                        </div>
                      </div>
                    </>
                  )
                },
                {
                  id: 'information-we-collect',
                  title: 'Information We Collect',
                  content: (
                    <>
                      <p>We collect personal information to provide you with a safe, customized, and efficient networking experience. This allows us to offer specialized features and services that accommodate your professional networking needs.</p>
                      <div className="mt-4 space-y-6">
                        <div>
                          <h3 className="font-semibold mb-2">During the registration process, we collect:</h3>
                          <ul className="list-disc pl-5 space-y-1">
                            <li>Names and professional titles</li>
                            <li>Company affiliations</li>
                            <li>Business email addresses</li>
                            <li>Professional profile information</li>
                          </ul>
                        </div>
                        <div>
                          <h3 className="font-semibold mb-2">For our NFC devices, we collect:</h3>
                          <ul className="list-disc pl-5 space-y-1">
                            <li>Device usage data</li>
                            <li>Profile sharing records</li>
                            <li>Connection timestamps</li>
                          </ul>
                        </div>
                        <div>
                          <h3 className="font-semibold mb-2">We automatically track certain information based upon your behavior on our site, including:</h3>
                          <ul className="list-disc pl-5 space-y-1">
                            <li>IP addresses</li>
                            <li>Browser information</li>
                            <li>Usage patterns</li>
                            <li>Website navigation data</li>
                          </ul>
                        </div>
                      </div>
                    </>
                  )
                },
                {
                  id: 'information-usage',
                  title: 'Information Usage',
                  content: (
                    <>
                      <p>We use personal information to:</p>
                      <ul className="list-disc pl-5 mt-4 space-y-1">
                        <li>Create and manage professional profiles</li>
                        <li>Enable NFC-based networking</li>
                        <li>Process payments through our secure payment provider</li>
                        <li>Provide customer support</li>
                        <li>Send service-related communications</li>
                        <li>Improve our services</li>
                      </ul>
                    </>
                  )
                },
                {
                  id: 'information-security',
                  title: 'Information Security',
                  content: (
                    <>
                      <p>We employ industry-standard protocols to protect personal information and reduce the risk of loss, misuse, or unauthorized access. All payment processing is handled by certified payment providers, and we do not store sensitive payment information.</p>
                      <div className="mt-4">
                        <h3 className="font-semibold mb-2">Our security measures include:</h3>
                        <ul className="list-disc pl-5 space-y-1">
                          <li>Data encryption</li>
                          <li>Secure servers</li>
                          <li>Access controls</li>
                          <li>Regular security assessments</li>
                        </ul>
                      </div>
                    </>
                  )
                },
                {
                  id: 'access-modify-or-delete-your-personal-information',
                  title: 'Access, Modify or Delete Your Personal Information',
                  content: (
                    <>
                      <p>You can access, update, or delete your personal information through your profile settings. If you cancel your account, you will not be able to use our services unless you re-register.</p>
                      <div className="mt-4">
                        <p>We retain information as long as necessary to:</p>
                        <ul className="list-disc pl-5 mt-2 space-y-1">
                          <li>Provide our services</li>
                          <li>Comply with legal obligations</li>
                          <li>Resolve disputes</li>
                          <li>Enforce agreements</li>
                        </ul>
                      </div>
                    </>
                  )
                },
                {
                  id: 'california-privacy-rights',
                  title: 'California Privacy Rights (CCPA)',
                  content: (
                    <>
                      <p>If you are a California resident, you have specific rights under the California Consumer Privacy Act (CCPA):</p>
                      <ul className="list-disc pl-5 mt-4 space-y-1">
                        <li>Right to know what personal information we collect and how we use it</li>
                        <li>Right to delete your personal information</li>
                        <li>Right to opt-out of the sale of personal information (note: we do not sell personal information)</li>
                        <li>Right to non-discrimination for exercising your rights</li>
                      </ul>
                      <p className="mt-4">California residents can exercise these rights by emailing privacy@bridgeonce.com.</p>
                    </>
                  )
                },
                {
                  id: 'gdpr-rights',
                  title: 'GDPR Rights',
                  content: (
                    <>
                      <p>Under the General Data Protection Regulation, EEA residents have the following rights:</p>
                      <ul className="list-disc pl-5 mt-4 space-y-1">
                        <li>Access your personal data</li>
                        <li>Correct inaccurate data</li>
                        <li>Request data deletion</li>
                        <li>Object to processing</li>
                        <li>Request data portability</li>
                        <li>Withdraw consent</li>
                      </ul>
                      <div className="mt-6">
                        <p>We process personal data under these legal bases:</p>
                        <ul className="list-disc pl-5 mt-2 space-y-1">
                          <li>Contract performance</li>
                          <li>Legitimate interests</li>
                          <li>Legal compliance</li>
                          <li>Consent where required</li>
                        </ul>
                      </div>
                      <div className="mt-6">
                        <p>When we transfer data internationally, we ensure appropriate safeguards through:</p>
                        <ul className="list-disc pl-5 mt-2 space-y-1">
                          <li>Standard contractual clauses</li>
                          <li>Adequacy decisions</li>
                          <li>Other legal transfer mechanisms</li>
                        </ul>
                      </div>
                    </>
                  )
                },
                {
                  id: 'contact-information',
                  title: 'Contact Information',
                  content: (
                    <>
                      <p>For privacy-related inquiries:</p>
                      <p className="mt-2">Email: privacy@bridgeonce.com</p>
                      <p className="mt-4">You have the right to lodge a complaint with your local data protection authority in the European Economic Area (EEA).</p>
                      <p className="mt-4">This Privacy Policy is effective as of January 18, 2025.</p>
                    </>
                  )
                }
              ].map((section) => (
                <div 
                  key={section.id}
                  id={section.id}
                  className="mb-12"
                >
                  <h2 className="text-2xl font-bold text-primary mb-4">
                    {section.title}
                  </h2>
                  <div className="text-text/80 space-y-4">
                    {section.content}
                  </div>
                </div>
              ))}
            </div>
          </motion.div>
        </motion.div>
      </main>
    </>
  );
} 